import React, { useCallback, useState } from 'react';
import { Field } from '@ontruck/orc';
import Tooltip from 'components/Tooltip';
import { PropTypes } from 'prop-types';
import { compose, pure } from 'recompose';
import FieldError from 'components/form/FieldError';

import DatePickerCalendar from 'components/form/DatePickerCalendar';
import ResponsiveModal from 'components/ResponsiveModal';

const enhance = compose(pure);

const noOp = () => {};

const DatePicker = ({
  id,
  t,
  label,
  renderValue,
  value,
  handleChange,
  minDate,
  maxDate,
  className,
  responsiveModalRef,
  fromWarning,
  error,
  errorLabel,
  calendarModifiers,
  tooltipText,
  disableWeekends,
  disabledDays,
  disabled,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const showErrorTooltip = error && errorLabel && isFocused;

  const handleOnFocus = useCallback(() => {
    setIsFocused(true);
  }, [setIsFocused]);

  const handleOnBlur = useCallback(() => {
    setIsFocused(false);
  }, [setIsFocused]);

  const renderField = () => (
    <div>
      {showErrorTooltip && <FieldError growMiddle>{t(errorLabel)}</FieldError>}
      <Field
        type="text"
        id={id}
        name={id}
        label={label}
        value={renderValue || label}
        labelType="hidden"
        size="L"
        fullWidth
        readOnly
        onChange={noOp}
        disabled={disabled}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        invalid={!!fromWarning}
        error={!!error}
      />
    </div>
  );
  return (
    <div className={className}>
      <ResponsiveModal
        modalRef={responsiveModalRef}
        refProperty="fieldRef"
        footer={t('footer')}
        alignRight={id.endsWith('dropoff_date') || id.endsWith('pickup_date')}
        content={
          <DatePickerCalendar
            value={value}
            minDate={minDate}
            maxDate={maxDate}
            handleChange={handleChange}
            calendarModifiers={calendarModifiers}
            disableWeekends={disableWeekends}
            disabledDays={disabledDays}
          />
        }
      >
        {!!tooltipText ? (
          <Tooltip
            component={t(tooltipText)}
            alwaysVisible
            segmentTitle="date picker"
            segmentData={{ label, value, error, errorLabel }}
          >
            {renderField()}
          </Tooltip>
        ) : (
          renderField()
        )}
      </ResponsiveModal>
    </div>
  );
};

DatePicker.propTypes = {
  value: PropTypes.instanceOf(Date),
  className: PropTypes.string,
  id: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  renderValue: PropTypes.node,
  handleChange: PropTypes.func,
  minDate: PropTypes.instanceOf(Date).isRequired,
  maxDate: PropTypes.instanceOf(Date),
  responsiveModalRef: PropTypes.func,
  fromWarning: PropTypes.string,
  label: PropTypes.string.isRequired,
  calendarModifiers: PropTypes.arrayOf(PropTypes.string),
  tooltipText: PropTypes.string,
  disabled: PropTypes.bool,
};

DatePicker.defaultProps = {
  value: null,
  className: '',
  handleChange: () => {},
  responsiveModalRef: () => {},
  maxDate: undefined,
  fromWarning: '',
};

export default enhance(DatePicker);
